import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAdmin, Table, ROLE_IT, fmtDate, eur } from "@/components/admin/AdminSections";
import { Ban, Trash2, CheckCircle2, Search } from "lucide-react";

const act = async (fn, msg, reload) => {
  try { await fn(); toast.success(msg); reload(); }
  catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
};

const UserActions = ({ u, reload }) => (
  <div className="flex gap-1.5">
    {["artist", "supplier"].includes(u.role) && (
      <select data-testid={`u-plan-${u.id}`} value={u.plan || "free"} onChange={(e) => act(() => api.post(`/admin/users/${u.id}/plan`, { plan: e.target.value }), "Piano aggiornato", reload)}
        className="px-2.5 py-1 rounded-full text-xs border border-slate-300 bg-white">
        <option value="free">Gratuito</option><option value="standard">Standard</option><option value="professional">Professional</option>
      </select>
    )}
    {u.role !== "admin" && (
      <>
        <button data-testid={`u-suspend-${u.id}`} onClick={() => (u.suspended || window.confirm(`Sospendere ${u.name}? Non potrà accedere e sparirà dal portale.`)) && act(() => api.post(`/admin/users/${u.id}/suspend`, { value: !u.suspended }), u.suspended ? "Profilo riattivato" : "Profilo sospeso", reload)}
          className={`px-2.5 py-1 rounded-full text-xs border flex items-center gap-1 ${u.suspended ? "bg-red-50 text-red-700 border-red-300" : "border-slate-300 hover:border-red-400"}`}><Ban className="w-3.5 h-3.5"/>{u.suspended ? "Riattiva" : "Sospendi"}</button>
        <button data-testid={`u-delete-${u.id}`} onClick={() => window.confirm(`Eliminare definitivamente ${u.name}?`) && act(() => api.delete(`/admin/users/${u.id}`), "Profilo eliminato", reload)}
          className="px-2.5 py-1 rounded-full text-xs border border-slate-300 hover:border-red-500 hover:text-red-600 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5"/>Elimina</button>
      </>
    )}
  </div>
);

export const UsersSection = () => {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [users, reload] = useAdmin("/admin/users", { q, role });
  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex flex-wrap gap-3">
        <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input data-testid="u-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome o email" className="pl-9 pr-4 py-2 border border-slate-300 rounded-full text-sm"/></div>
        <select data-testid="u-role" value={role} onChange={(e) => setRole(e.target.value)} className="border border-slate-300 rounded-full px-4 py-2 text-sm">
          <option value="all">Tutti i ruoli</option>{Object.entries(ROLE_IT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {users && <Table testId="u-table" cols={["Nome", "Email", "Ruolo", "Iscritto il", "Piano", "Azioni"]} empty="Nessun utente"
        rows={users.map((u) => [
          ["artist", "supplier"].includes(u.role) ? <Link to={`/artista/${u.id}`} className="font-semibold hover:text-gold">{u.name}</Link> : u.name,
          u.email, ROLE_IT[u.role] || u.role, fmtDate(u.createdAt),
          ["artist", "supplier"].includes(u.role) ? ({ free: "Gratuito", standard: "Standard", professional: "Professional" })[u.plan] || "Gratuito" : "-",
          <span className="flex items-center gap-2">{u.suspended && <span className="px-2 py-0.5 rounded-full text-[11px] bg-red-100 text-red-700 font-semibold" data-testid={`u-suspended-${u.id}`}>Sospeso</span>}<UserActions u={u} reload={reload}/></span>])}/>}
    </div>
  );
};

export const ModerationSection = () => {
  const [d, reload] = useAdmin("/admin/moderation");
  if (!d) return <p className="text-sm text-slate-500">Caricamento…</p>;
  return (
    <div className="space-y-8" data-testid="admin-moderation">
      <div>
        <div className="overline text-gold mb-3">Nuovi profili da verificare ({d.newProfiles.length})</div>
        <Table testId="mod-profiles" cols={["Profilo", "Email", "Ruolo", "Citt\u00e0", "Iscritto il", "Azioni"]} empty="Nessun profilo da verificare"
          rows={d.newProfiles.map((u) => [<Link to={`/artista/${u.id}`} className="font-semibold hover:text-gold">{u.name}</Link>, u.email, ROLE_IT[u.role], u.city || "-", fmtDate(u.createdAt),
            <div className="flex gap-1.5">
              <button data-testid={`mod-approve-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/approve`), "Profilo approvato", reload)} className="px-2.5 py-1 rounded-full text-xs bg-emerald-600 text-white flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5"/>Approva</button>
              <UserActions u={{ ...u, suspended: u.suspended }} reload={reload}/>
            </div>])}/>
      </div>
      <div>
        <div className="overline text-gold mb-3">Segnalazioni ricevute ({d.reports.length})</div>
        <Table testId="mod-reports" cols={["Profilo segnalato", "Da", "Motivo", "Data", "Azioni"]} empty="Nessuna segnalazione aperta"
          rows={d.reports.map((r) => [<Link to={`/artista/${r.targetUserId}`} className="font-semibold hover:text-gold">{r.targetName}</Link>, r.fromName,
            <span className="whitespace-normal block max-w-xs">{r.reason}</span>, fmtDate(r.createdAt),
            <div className="flex gap-1.5">
              <button data-testid={`rep-resolve-${r.id}`} onClick={() => act(() => api.post(`/admin/reports/${r.id}/resolve`), "Segnalazione chiusa", reload)} className="px-2.5 py-1 rounded-full text-xs border border-slate-300 hover:border-gold">Chiudi</button>
              <button data-testid={`rep-suspend-${r.id}`} onClick={() => act(async () => { await api.post(`/admin/users/${r.targetUserId}/suspend`, { value: true }); await api.post(`/admin/reports/${r.id}/resolve`); }, "Profilo sospeso", reload)} className="px-2.5 py-1 rounded-full text-xs border border-red-300 text-red-700">Sospendi profilo</button>
            </div>])}/>
      </div>
      <div>
        <div className="overline text-gold mb-3">ADV in attesa di approvazione ({d.pendingAds.length})</div>
        <Table testId="mod-ads" cols={["Inserzionista", "Offerta", "Durata", "Totale", "Azioni"]} empty="Nessuna inserzione in attesa"
          rows={d.pendingAds.map((a) => [a.advertiser, <span className="whitespace-normal block max-w-xs">{a.offerText}</span>, `${a.days} gg`, eur(a.totalGross),
            <button data-testid={`mod-ad-approve-${a.id}`} onClick={() => act(() => api.post(`/ads/${a.id}/approve`), "Inserzione approvata", reload)} className="px-2.5 py-1 rounded-full text-xs bg-emerald-600 text-white">Approva</button>])}/>
      </div>
    </div>
  );
};
